import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fetchUserProducts, deleteProductApi, fetchSellers } from '../../services/user';
import { useTheme } from '../../context/ThemeContext';
import { ImportProductModal } from '../../components/Product/ImportProductModal';
import { DeleteConfirmModal } from '../../components/Product/DeleteConfirmModal';
import ExportModal from '../../components/ExportModal';

interface Product {
  id: number;
  productName: string;
  price: number;
  maxPrice: number;
  productImage: any;
  productCategory: string;
  productSubcategory: string;
  quantity: number;
  productDescription: string;
  UOMvalue: string;
  UOM: string;
}

const SearchableSellerDropdown = ({ sellers, value, onChange }: { sellers: any[], value: string, onChange: (val: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = sellers.filter(s =>
    (s.businessName || s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedSeller = sellers.find(s => s._id === value);
  const displayValue = value === 'all' ? 'All Sellers' : (selectedSeller ? `${selectedSeller.businessName || selectedSeller.name}` : 'Select Seller');

  return (
    <div className="relative z-50">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md bg-white cursor-pointer relative"
      >
        <span className="block truncate">{displayValue}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <input
                type="text"
                autoFocus
                placeholder="Search sellers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 shadow-inner"
              />
            </div>
            <ul className="max-h-60 overflow-auto py-1">
              <li
                className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm ${value === 'all' ? 'bg-green-100 font-medium' : 'text-gray-700'}`}
                onClick={() => { onChange('all'); setIsOpen(false); setSearch(''); }}
              >
                All Sellers
              </li>
              {filtered.map(s => (
                <li
                  key={s._id}
                  className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm ${value === s._id ? 'bg-green-100 font-medium' : 'text-gray-700'}`}
                  onClick={() => { onChange(s._id); setIsOpen(false); setSearch(''); }}
                >
                  <div className="font-medium">{s.businessName || s.name}</div>
                  <div className="text-xs text-gray-500 truncate">{s.email}</div>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-sm text-gray-500 text-center italic">No sellers found</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

const ProductListPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial page only from URL query param (?page=4)
  const getInitialPage = (): number => {
    const urlPage = parseInt(searchParams.get('page') || '', 10);
    if (urlPage && urlPage > 0) return urlPage;
    return 1;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(getInitialPage);
  const [productsPerPage] = useState(12);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 20000]);
  const [sortOption, setSortOption] = useState<string>('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [selectedSeller, setSelectedSeller] = useState<string>('all');
  const [stockStatus, setStockStatus] = useState<string>('all');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Local state for the modal
  const [localCategory, setLocalCategory] = useState<string>('all');
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([0, 20000]);
  const [localStockStatus, setLocalStockStatus] = useState<string>('all');
  const [localSeller, setLocalSeller] = useState<string>('all');

  const [orgSellers, setOrgSellers] = useState<any[]>([]);
  const { role } = useTheme();
  const navigate = useNavigate();

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (priceRange[0] !== 0 || priceRange[1] !== 20000) count++;
    if (stockStatus !== 'all') count++;
    if (selectedSeller !== 'all') count++;
    return count;
  };
  const activeFiltersCount = getActiveFiltersCount();

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      const res = await deleteProductApi(deleteTarget.id);
      if (res.success !== false) {
        toast.success(res.message || 'Product deleted successfully');
        setDeleteTarget(null);
        fetchProducts();
      } else {
        toast.error(res.message || 'Failed to delete product');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err.message || 'Error deleting product');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (role === 'ORGANISATION') {
      const orgId = localStorage.getItem('ud-user');
      if (orgId) {
        fetchSellers(orgId, 1, '', '', '', 1000).then(res => {
          if (res.sellers) {
            setOrgSellers(res.sellers);
          }
        }).catch(err => console.error("Error fetching sellers:", err));
      }
    }
  }, [role]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userId = localStorage.getItem('ud-user') ?? '';

      const requestData: any = {
        userId,
        page: currentPage,
        limit: productsPerPage,
        search: searchTerm,
        category: selectedCategory,
        sortOption: sortOption,
        sellerId: selectedSeller,
        stockStatus: stockStatus
      };

      if (priceRange[0] > 0) requestData.minPrice = priceRange[0];
      if (priceRange[1] < 20000) requestData.maxPrice = priceRange[1];

      const response = await fetchUserProducts(requestData);

      const rawData = response.data;
      const productList = Array.isArray(rawData)
        ? rawData
        : (Array.isArray(rawData?.products) ? rawData.products : []);

      setProducts(productList);

      if (response.availableCategories) {
        setAvailableCategories(response.availableCategories);
      }
      if (response.totalRecords !== undefined) {
        setTotalRecords(response.totalRecords);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to page 1 ONLY when user actually changes filters/search (skip initial mount)
  const isFilterMount = useRef(true);
  useEffect(() => {
    if (isFilterMount.current) {
      isFilterMount.current = false;
      return;
    }
    setCurrentPage(1);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('page');
        return newParams;
      },
      { replace: true }
    );
  }, [searchTerm, selectedCategory, priceRange, sortOption, selectedSeller, stockStatus]);

  // Sync page state when URL searchParams change (e.g. browser back/forward or sidebar click)
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '', 10);
    const targetPage = urlPage && urlPage > 0 ? urlPage : 1;
    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
    }
  }, [searchParams]);

  // Clean up any old sessionStorage key
  useEffect(() => {
    sessionStorage.removeItem('product_list_page');
  }, []);

  // Fetch data on state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, selectedCategory, priceRange, sortOption, selectedSeller, stockStatus]);

  const currentProducts = products;
  const totalPages = Math.ceil(totalRecords / productsPerPage) || 1;

  // Safeguard: if totalRecords change and currentPage > totalPages
  useEffect(() => {
    if (totalRecords > 0) {
      const maxPages = Math.ceil(totalRecords / productsPerPage) || 1;
      if (currentPage > maxPages) {
        paginate(maxPages);
      }
    }
  }, [totalRecords, productsPerPage]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (pageNumber > 1) {
          newParams.set('page', String(pageNumber));
        } else {
          newParams.delete('page');
        }
        return newParams;
      },
      { replace: true }
    );
  };

  const handleProductClick = (productId: any) => {
    navigate(`/products/details/${productId}`, {
      state: { fromPage: currentPage }
    });
  };
  // const navigateToAddProduct: () => void = ()=> navigate('/products/add');

  // Construct category dropdown from server-provided available categories
  const categories = [
    { value: 'all', label: 'All Categories' },
    ...availableCategories
      .filter(category => category)
      .map(category => ({
        value: category,
        label: category?.charAt(0)?.toUpperCase() + category?.slice(1)
      }))
  ];

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center py-12">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Products</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <div className="flex justify-between gap-4">
            {/* <button onClick={() => navigate('/products/add')} className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-200">Add Product</button> */}
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center gap-2 px-4 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition duration-200 font-medium whitespace-nowrap"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="w-full px-4 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-200 font-medium"
            >
              Import Products
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search products..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Sort Dropdown */}
            <div className="w-full md:w-auto">
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>

            {/* Filter Buttons */}
            <div className="flex space-x-2">
              {activeFiltersCount > 0 && (
                <button
                  className="flex items-center justify-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  onClick={() => {
                    setSelectedCategory('all');
                    setPriceRange([0, 20000]);
                    setStockStatus('all');
                    setSelectedSeller('all');
                  }}
                >
                  <FiX className="mr-1" size={16} />
                  Clear Filters
                </button>
              )}
              <button
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                onClick={() => {
                  setLocalCategory(selectedCategory);
                  setLocalPriceRange(priceRange);
                  setLocalStockStatus(stockStatus);
                  setLocalSeller(selectedSeller);
                  setShowFilters(true);
                }}
              >
                <FiFilter className="mr-2" size={16} />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Standard Filters Modal */}
          {showFilters && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-visible">

                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Advanced Filters</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <FiX size={24} />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-visible z-[1] p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Category Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                        value={localCategory}
                        onChange={(e) => setLocalCategory(e.target.value)}
                      >
                        {categories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Price Range Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price Range: ₹{localPriceRange[0]} - ₹{localPriceRange[1]}
                      </label>
                      <div className="flex items-center space-x-4 pt-2">
                        <input
                          type="range"
                          min="0"
                          max="20000"
                          step="100"
                          value={localPriceRange[0]}
                          onChange={(e) => setLocalPriceRange([parseInt(e.target.value), localPriceRange[1]])}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <input
                          type="range"
                          min="0"
                          max="20000"
                          step="100"
                          value={localPriceRange[1]}
                          onChange={(e) => setLocalPriceRange([localPriceRange[0], parseInt(e.target.value)])}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                      </div>
                    </div>

                    {/* Stock Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stock Status</label>
                      <select
                        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                        value={localStockStatus}
                        onChange={(e) => setLocalStockStatus(e.target.value)}
                      >
                        <option value="all">All Items</option>
                        <option value="in-stock">In Stock Only</option>
                        <option value="out-of-stock">Out of Stock Only</option>
                      </select>
                    </div>

                    {/* Seller Filter */}
                    {role === 'ORGANISATION' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Seller</label>
                        <SearchableSellerDropdown
                          sellers={orgSellers}
                          value={localSeller}
                          onChange={setLocalSeller}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategory(localCategory);
                      setPriceRange(localPriceRange);
                      setStockStatus(localStockStatus);
                      setSelectedSeller(localSeller);
                      setShowFilters(false);
                    }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Apply & Search
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900">No products found</h3>
            <p className="mt-2 text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentProducts.map((product: any) => (
                <div
                  key={product._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                  onClick={() => handleProductClick(product._id)}
                >
                  {/* Product Image */}
                  <div className="relative pb-[75%] bg-gray-100">
                    <img
                      src={product.productImage[0]}
                      alt={product.productName}
                      className="absolute h-full w-full object-cover"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 flex-1">{product.productName}</h3>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <button
                          title="Edit Product"
                          className="p-1.5 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/products/edit/${product._id}`, {
                              state: { fromPage: currentPage }
                            });
                          }}
                        >
                          <FiEdit size={16} />
                        </button>
                        <button
                          title="Delete Product"
                          className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: product._id, name: product.productName });
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.productDescription}</p>

                    {/* Price */}
                    <div className="flex items-center mb-2">
                      {product.price ? (
                        <>
                          <span className="text-lg font-bold text-gray-900">
                            {product.price}
                          </span>
                          <span className="ml-2 text-sm text-gray-500 line-through">
                            {product.maxPrice}
                          </span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          {product.price}
                        </span>
                      )}
                    </div>

                    {/* Stock Status */}
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${product.quantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                    >
                      {product.quantity > 0 ? `${product.quantity} in stock` : 'Out of stock'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <FiChevronLeft className="mr-1" />
                  Previous
                </button>

                <div className="hidden md:flex space-x-1">
                  {getPageNumbers().map((number, idx) => (
                    number === '...' ? (
                      <span key={`dots-${idx}`} className="px-4 py-2 text-gray-500">...</span>
                    ) : (
                      <button
                        key={number}
                        onClick={() => paginate(number as number)}
                        className={`px-4 py-2 border text-sm font-medium rounded-md ${currentPage === number
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {number}
                      </button>
                    )
                  ))}
                </div>

                <div className="md:hidden text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </div>

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Next
                  <FiChevronRight className="ml-1" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ImportProductModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={fetchProducts}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        productName={deleteTarget?.name || ''}
        isDeleting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportType="products"
        orgSellers={orgSellers}
      />
    </div>
  );
};

export default ProductListPage;